import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { HSLColor, contrastRatio } from '@yola/yola-palette-generator';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import parseHslColorString from 'src/js/modules/website-design/helpers/parse-hsl-color-string';
import CustomPaletteDialog from '../components/custom-palette-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import customColorOptions from '../constants/custom-color-options';
import helpers from '../helpers';
import segment from '../../analytics/segment';
import advancedVariableIds from '../constants/advanced-variable-ids';
import triggerIds from '../constants/trigger-ids';
import advancedVariableCategoryIds from '../constants/advanced-variable-category-ids';
import useInjectPaletteToTemplate from '../hooks/use-inject-palette-to-template';
import { colorToSurfaceMap } from '../constants/colors-maps';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  generateCustomPaletteId,
  updatePalettesWith,
  resetColorSchemeColors,
  generatePalette,
  isBaseColorChanged,
} = helpers;

const getCaptions = () => ({
  title: i18next.t('Edit color palette'),
  description: i18next.t(
    'The color palette includes 6 customizable colors that control the overall appearance of your site'
  ),
  titleBreadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  customPaletteLabel: i18next.t('Custom palette'),
  colorSchemesLabel: i18next.t('Block color schemes'),
  resetTooltipLabel: i18next.t('Reset'),
  popoverInfo: i18next.t(
    'Your palette is used to generate 6 color schemes that can be applied to your blocks'
  ),
  buttons: {
    cancel: i18next.t('Cancel'),
    submit: i18next.t('Save'),
    editPalette: i18next.t('Edit palette'),
  },
  colors: {
    altLight: i18next.t('Light color scheme'),
    light: i18next.t('Light-grey color scheme'),
    dark: i18next.t('Dark color scheme'),
    altDark: i18next.t('Dark-grey color scheme'),
    primary: i18next.t('Primary color scheme'),
    secondary: i18next.t('Secondary color scheme'),
  },
  resetDialog: {
    title: i18next.t('Confirm color reset'),
    description: i18next.t(
      "All your color adjustments will be reset to the default color palette. This action can't be undone."
    ),
    submit: i18next.t('Confirm'),
    cancel: i18next.t('Cancel'),
  },
});

function CustomPaletteContainer(props) {
  const dispatch = useDispatch();
  const { getSharedData, onDialogMainAction, onDialogCancel, resolveSharedData } = props;
  const {
    offsetX,
    offsetY,
    originPalette,
    editPalette,
    activePalette,
    isEdit,
    customColorPalettes,
  } = getSharedData();

  const [dialogPosition, updatePosition] = useState({
    offsetX,
    offsetY,
  });
  const parentPalette = useSelector((state) =>
    customization.selectors.getColorPalette(state, originPalette.parentId || editPalette?.parentId)
  );

  const colorPaletteConfig = useSelector(customization.selectors.getColorPaletteConfig);
  const isDeveloperModeEnabled = isDeveloperMode();
  const captions = getCaptions();

  const onClose = () =>
    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
        sourceId: dialogCallSourceIds.BACK_FROM_CUSTOM_PALETTE,
      })
    );

  const updateSharedData = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const injectPaletteToTemplate = useInjectPaletteToTemplate();

  const getAdjustedColors = (oldPalette, newPalette) =>
    Object.keys(oldPalette.colors).filter(
      (colorKey) => oldPalette.colors[colorKey] !== newPalette.colors[colorKey]
    );

  const genNewPaletteId = () => {
    const palettesIds = customColorPalettes.map(({ id }) => id);
    return generateCustomPaletteId(palettesIds);
  };

  const getEditPalette = () => {
    const palette = editPalette || { ...originPalette };

    if (!isEdit && palette.id === originPalette.id) {
      palette.id = genNewPaletteId();
      palette.parentId = originPalette.id;
    }

    return palette;
  };

  const currentEditPalette = getEditPalette();

  const handleSubmit = ({ isClickOnOverlay } = {}) => {
    updateSharedData({
      activeColorPaletteId: currentEditPalette.id,
      updatedPalettes: updatePalettesWith(currentEditPalette, customColorPalettes),
      editPalette: null,
    });

    onDialogMainAction({
      triggerId: isClickOnOverlay ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      colorsAdjusted: getAdjustedColors(originPalette, currentEditPalette),
    });
    onClose();
  };

  const handleCancel = () => {
    injectPaletteToTemplate(activePalette);

    updateSharedData({
      activeColorPaletteId: activePalette.id,
      editPalette: null,
      updatedPalettes: customColorPalettes,
    });
    onDialogCancel();
    onClose();
  };

  const onOverlayClick = () => {
    handleSubmit({ isClickOnOverlay: true });
  };

  const handleColorSelect = ({ colorKey, isDevMode = false }) => {
    updateSharedData({ editPalette: currentEditPalette, isDeveloperMode: isDevMode });

    dispatch(
      dialogs.actions.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorKey,
        triggerId: triggerIds.COLOR_SCHEME,
      })
    );
  };

  const onDeveloperModeClick = ({ colorKey }) => handleColorSelect({ colorKey, isDevMode: true });

  const {
    colors,
    advancedColors,
    variables: { wsColorTextWhite },
  } = currentEditPalette;

  const colorSchemes = useMemo(() => {
    const whiteHSL = new HSLColor(
      ...Object.values(parseHslColorString(`hsl(${wsColorTextWhite})`))
    );

    return Object.keys(colors).reduce((acc, colorKey) => {
      const colorValue = colors[colorKey];

      const colorValueHSL = new HSLColor(...Object.values(parseHslColorString(colorValue)));
      const isDarkTheme = contrastRatio(colorValueHSL, whiteHSL) >= customColorOptions.LIGHT_CR;

      const colorPalette = editPalette || originPalette;
      const surface = colorToSurfaceMap[colorKey];
      const hasAdvancedColors = Object.keys(colorPalette.advancedColors || {}).some((key) =>
        key.includes(surface)
      );

      const isResetAvailable = isBaseColorChanged(colorKey, colorPalette) || hasAdvancedColors;

      return {
        ...acc,
        [colorKey]: {
          value: colorValue,
          isDarkTheme,
          isResetAvailable,
        },
      };
    }, {});
  }, [colors, wsColorTextWhite, editPalette, originPalette]);

  const handleColorSchemeReset = ({ colorKey }) => {
    const colorPalette = editPalette || originPalette;

    const compiledPalette = generatePalette(
      resetColorSchemeColors({
        colorKey,
        colorPalette,
        parentPalette,
      }),
      colorPaletteConfig
    );

    updateSharedData({
      editPalette: compiledPalette,
    });

    injectPaletteToTemplate(compiledPalette);

    trackEvent(events.DESIGN_SETTINGS_RESET_TRIGGER_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE,
      triggerId: triggerIds.COLOR_SCHEME_SETTINGS,
      colorsAdjusted: [colorKey],
      variableId: advancedVariableIds.BACKGROUND_COLOR,
      variableCategory: advancedVariableCategoryIds.GENERAL,
    });
  };

  const onEditPaletteClick = () => {
    dispatch(
      dialogs.actions.show(dialogTypes.BASE_COLORS_PICKER_DIALOG, {
        triggerId: triggerIds.COLOR_PALETTE_SETTINGS,
        colorPreset: originPalette.id,
      })
    );

    updateSharedData();
  };

  return (
    <CustomPaletteDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      colorSchemes={colorSchemes}
      onColorSelect={handleColorSelect}
      onDragEnd={handleDragEnd}
      advancedColors={advancedColors}
      onOverlayClick={onOverlayClick}
      onColorSchemeReset={handleColorSchemeReset}
      isDeveloperModeEnabled={isDeveloperModeEnabled}
      onDeveloperModeClick={onDeveloperModeClick}
      onEditPaletteClick={onEditPaletteClick}
    />
  );
}

CustomPaletteContainer.propTypes = {
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default CustomPaletteContainer;
